import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box3, Camera, Group, Object3D, OrthographicCamera, Vector3, WebGLRenderer } from 'three';

import { GeodataBoundaryPolygonModel } from '../../../../../../../../../typings/api/skymap/rest/v1/.common';
import { isDefined } from '../../../../../../../utils/variables';
import { CoordinateManager } from '../../../../../../../viewer/coordinate-manager';
import { refreshWmsViewerProjection } from '../../../../../../../viewer/wms-state';
import { useObject3d } from '../../../../../../hooks/three/use-object-3d';
import { useOrbitControls } from '../../../../../../hooks/three/use-orbit-controls';
import { useWms } from '../../../../../../hooks/three/use-wms';
import { ProjectSpatialReferenceContext } from '../../../../../../state/project-reference-state';
import { useSfmRenderingContext } from '../../../../../cloud-processing/gcp-page/use-sfm-rendering-context';
import { loadBoundaryPolygonGlb, LoadedBoundaryPolygon } from './load-boundary-polygon-glb';

const PreviewContainer = styled.div`
  width: 450px;
  height: 450px;
  background-color: black;
  border-radius: 4px;
  margin-top: 8px;
  position: relative;
  overflow: hidden;
`;

const WmsRenderTarget = styled.div`
  position: absolute;

  height: 2048px;
  width: 2048px;

  visibility: hidden;
`;

const OlCanvas = styled.div`
  position: absolute;

  height: 100%;
  width: 100%;
`;

interface BoundaryPolygonPreviewProps {
  boundaryPolygons: GeodataBoundaryPolygonModel[];
}

// Hook to load boundary polygon data and create 3D objects
export function useBoundaryPolygonLoader(
  boundaryPolygons: GeodataBoundaryPolygonModel[],
  proj4Definition: string | undefined,
) {
  const [boundaryPolygonObjects, setBoundaryPolygonObjects] = useState<Object3D[]>([]);
  const [loadingStatus, setLoadingStatus] = useState<
    Record<string, 'idle' | 'loading' | 'loaded' | 'error'>
  >({});
  const [boundingBox, setBoundingBox] = useState<Box3 | null>(null);
  const [origo, setOrigo] = useState<Vector3 | null>(null);
  const [projection, setProjection] = useState<string>();

  // Filter boundary polygons that have GLB data URLs
  const glbBoundaryPolygons = useMemo(() => {
    return boundaryPolygons.filter(
      (polygon: GeodataBoundaryPolygonModel) =>
        polygon.simDataUrl && polygon.enabled && polygon.status === 'COMPLETED',
    );
  }, [boundaryPolygons]);

  const updateOrigo = useCallback(
    (loadedResults: LoadedBoundaryPolygon[], oldOrigo: Vector3 | null) => {
      const combinedBox = new Box3();
      loadedResults.forEach((box) => combinedBox.union(box.boundingBox));

      // Set origo as the min of the combined bounding box
      const newOrigo = combinedBox.min.clone();

      // Check if origo has moved more than threshold (100 units)
      const currentOrigo = oldOrigo || new Vector3();
      const origoMoved = newOrigo.distanceTo(currentOrigo) > 100;

      let finalOrigo: Vector3;
      if (origoMoved || !oldOrigo) {
        if (proj4Definition) {
          CoordinateManager.getInstance().setProjectProjectionData(proj4Definition, newOrigo);
          setProjection(proj4Definition);
        }
        finalOrigo = newOrigo;
      } else {
        finalOrigo = currentOrigo;
      }

      // Bounding box in local coordinates.
      setBoundingBox(
        new Box3(combinedBox.min.clone().sub(finalOrigo), combinedBox.max.clone().sub(finalOrigo)),
      );

      // Position each boundary group based on offset vs origo difference
      loadedResults.forEach(({ boundaryGroup, offset }) => {
        const positionOffset = offset.clone().sub(finalOrigo);
        boundaryGroup.position.copy(positionOffset);
      });

      return finalOrigo;
    },
    [proj4Definition],
  );

  // Load GLB data for each boundary polygon
  useEffect(() => {
    const loadGlbData = async () => {
      const newLoadingStatus: Record<string, 'idle' | 'loading' | 'loaded' | 'error'> = {};
      const loadedResults: LoadedBoundaryPolygon[] = [];

      for (const polygon of glbBoundaryPolygons) {
        newLoadingStatus[polygon.id] = 'loading';

        const result = await loadBoundaryPolygonGlb(polygon);

        if (result.success) {
          loadedResults.push(result);
          newLoadingStatus[polygon.id] = 'loaded';
        } else {
          console.error(`Failed to load GLB for boundary polygon ${polygon.id}:`, result.error);
          newLoadingStatus[polygon.id] = 'error';
        }
      }

      // Calculate combined bounding box and origo
      if (loadedResults.length > 0) {
        setOrigo((oldOrigo) => updateOrigo(loadedResults, oldOrigo));
      } else {
        setBoundingBox(null);
        setOrigo(null);
        setProjection(undefined);
      }

      setBoundaryPolygonObjects(loadedResults.map((result) => result.boundaryGroup));
      setLoadingStatus(newLoadingStatus);
    };

    if (glbBoundaryPolygons.length > 0) {
      void loadGlbData();
    } else {
      setBoundaryPolygonObjects([]);
      setLoadingStatus({});
      setBoundingBox(null);
    }
  }, [glbBoundaryPolygons, proj4Definition, updateOrigo]);

  // Create a single Object3D group containing all boundary polygon objects
  const boundaryPolygonsGroup = useMemo(() => {
    const group = new Group();
    group.name = 'boundary-polygons';

    boundaryPolygonObjects.forEach((obj) => {
      group.add(obj);
    });

    return group;
  }, [boundaryPolygonObjects]);

  return {
    boundaryPolygonsGroup,
    boundaryPolygonObjects,
    glbBoundaryPolygons,
    loadingStatus,
    boundingBox,
    origo,
    projection,
  };
}

export const BoundaryPolygonPreview: React.FC<BoundaryPolygonPreviewProps> = ({
  boundaryPolygons,
}) => {
  const webGlContainer = useRef<HTMLDivElement>(null);
  const wmsRenderTarget = useRef<HTMLDivElement>(null);
  const cameraInitialized = useRef(false);
  const { coordinateSystem } = React.useContext(ProjectSpatialReferenceContext);
  const wmsUpdateRef = useRef<((camera: Camera, renderer: WebGLRenderer) => void) | undefined>(
    undefined,
  );
  const { webGlScene, camera, setCameraType } = useSfmRenderingContext(webGlContainer, {
    onBeforeRenderRef: wmsUpdateRef,
  });
  const { boundaryPolygonsGroup, boundingBox, origo, projection } = useBoundaryPolygonLoader(
    boundaryPolygons,
    coordinateSystem?.proj4,
  );

  const controls = useOrbitControls(webGlContainer, camera, boundingBox);

  // Register proj4 'wms' so convertWmsExtentsToLocalExtents can use it (must be defined before map renders).
  useEffect(() => {
    if (isDefined(projection) && projection.trim() !== '') {
      refreshWmsViewerProjection(projection);
    }
  }, [projection]);

  const isOrthoView = useMemo(() => camera instanceof OrthographicCamera, [camera]);

  // WMS setup - show WMS when we have projection (CoordinateManager and 'wms' are set by loader / effect above).
  const { wmsQuad } = useWms(
    wmsRenderTarget,
    controls,
    isOrthoView,
    wmsUpdateRef,
    projection,
    origo,
  );

  // Add boundary polygons and WMS to the scene. Put WMS quad in webGlScene (same as polygon)
  // so it renders in the same pass with lower renderOrder; wmsScene two-pass can be unreliable in this context.
  useObject3d(webGlScene, boundaryPolygonsGroup);
  useObject3d(webGlScene, wmsQuad);

  // Reset camera initialization when boundary polygons change
  useEffect(() => {
    cameraInitialized.current = false;
  }, [boundaryPolygons]);

  // Set camera to orthographic view and focus on boundary polygons (only once)
  useEffect(() => {
    if (
      cameraInitialized.current ||
      !boundingBox ||
      !origo ||
      !(camera instanceof OrthographicCamera)
    ) {
      return;
    }

    setCameraType('orthographic');

    const center = boundingBox.getCenter(new Vector3());
    const size = boundingBox.getSize(new Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Set camera position to view the boundary polygons (relative to origo)
    camera.position.set(center.x, center.y, center.z + maxDim * 2);
    camera.lookAt(center);

    // Set orthographic camera bounds
    const halfWidth = maxDim * 0.6;
    const halfHeight = maxDim * 0.6;
    camera.left = -halfWidth;
    camera.right = halfWidth;
    camera.top = halfHeight;
    camera.bottom = -halfHeight;
    camera.near = 0.1;
    camera.far = maxDim * 10;
    camera.updateProjectionMatrix();

    // Update controls target
    if (controls) {
      controls.target.copy(center);
      controls.update();
      // Trigger WMS extent update so the map fits the view (useWms listens to orbit 'end' / 'change')
      controls.dispatchEvent({ type: 'end' });
      // Run again after the next frame so the map gets a chance to paint (fixes initial render before first drag)
      requestAnimationFrame(() => {
        controls.update();
        controls.dispatchEvent({ type: 'end' });
      });
    }

    // Mark camera as initialized to prevent further updates
    cameraInitialized.current = true;
  }, [boundingBox, origo, camera, setCameraType, controls]);

  return (
    <PreviewContainer>
      <div ref={webGlContainer} style={{ width: '100%', height: '100%' }} />
      <WmsRenderTarget>
        <OlCanvas ref={wmsRenderTarget} />
      </WmsRenderTarget>
    </PreviewContainer>
  );
};
